import {
	Component, Input, ElementRef, SimpleChanges, ViewChildren, QueryList,
	EventEmitter, Output, HostListener, ViewChild, OnInit, OnChanges, OnDestroy
} from '@angular/core';
import { CdkDragDrop, CdkDragStart, CdkDragEnd, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription, combineLatest } from 'rxjs';

import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { ITrackAreaItem } from '../../interfaces/ITrackAreaItem';
import { Track } from '../../classes/Track';
import { TrackGroup } from '../../classes/TrackGroup';
import { InteractivityWidgetSelectionService } from '../../services/interactivity-widget-selection.service';
import { IInCurrentTimeCollection } from '../../interfaces/IWidgetSelection';
import { WidgetType } from '../../enums/WidgetType';
import { TranslateService } from '@ngx-translate/core';
import { InteractivityStateService } from '../../services/interactivity-state.service';
import { modKeyDown } from '../../../../../shared/utils/mod-key-down.util';
import { BbResizableScrollbarComponent } from '../../../../../components/shared/bb-resizable-scrollbar/bb-resizable-scrollbar.component';
import { InteractivityMediaService } from '../../services/interactivity-media.service';
import { IInOutPoint, IWidgetGroupTag } from '../../redux/timeline-data.model';
import { PillboxConfiguration } from '../../../../../components/shared/bb-pillbox/bb-pillbox.component';
import { IWidget } from '../../interfaces/IWidget';
import { ContextMenuOption } from '../../../../../components/shared/bb-context-menu/bb-context-menu.component';
import { StageWidget } from '../../classes/StageWidget';
import { BbScrollablePaneComponent } from '../../../../../components/shared/bb-scrollable-pane/bb-scrollable-pane.component';
import { SecondsPipe } from '../../../../../pipes/seconds.pipe';

export interface FlatITrackAreaItem {
	id?: string;
	groupId?: string;
	type: string;
	child?: ITrackAreaItem;
}

export interface TimepointConfiguration {
	id?: string;
	pillboxConfig?: PillboxConfiguration[];
	icon?: string;
	timepoints: any;
	label?: string;
}

@Component({
	selector: 'tracks',
	templateUrl: './tracks.component.html',
	styleUrls: ['./tracks.component.scss']
})

export class TracksComponent implements OnInit, OnChanges, OnDestroy {

	@Input() showGhost: boolean;
	@Input() widgetType: WidgetType;
	@Output() onTrackSelected: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('scrollablePane', { static: true }) scrollablePane: BbScrollablePaneComponent;
	@ViewChildren('track', { read: ElementRef }) tracksEl: QueryList<ElementRef>;
	@ViewChild('tracksList', { static: true }) cdkDropList: ElementRef;
	@ViewChild('verticalScrollbar', { static: true }) verticalScrollbar: BbResizableScrollbarComponent;

	public editMode = false;
	public draggingTrack = false;
	public ghostTrackIndex: number;
	public groupIndex: number;
	public items: ITrackAreaItem[];		// ITrackAreaItem can be Track OR TrackGroup

	// TODO: implement allDropLists or remove from html
	allDropLists: any;

	chapterTracks: ITrackAreaItem[];
	flatArray: FlatITrackAreaItem[] = [];
	freeTextFilter = '';
	hiddenTracks: string[] = [];
	selectedTrackIds: string[] = [];
	filterableWidgetTypes: string[] = [];
	widgetGroupTags: IWidgetGroupTag[] = [];
	activeWidgetTypeFilters: string[] = [];
	activeWidgetGroupIDs: string[] = [];
	collapsedGroups: any = {};
	currentDragFlatTrack: FlatITrackAreaItem;
	inCurrentTime: IInCurrentTimeCollection;
	chapterConfig: TimepointConfiguration;
	contextMenuStyle: any = { display: 'none' };
	contextMenuOptions: ContextMenuOption[] = [];
	trackZoomInfo: any;

	private subscriptions: Subscription;
	private selectedTracks: any = [];
	private duration = 0;
	private _currentTime = 0;
	private cachedAppTrack: ITrackAreaItem = null;


	constructor(public interactivityEditorService: InteractivityEditorService,
		private interactivityMediaService: InteractivityMediaService,
		private interactivityStateService: InteractivityStateService,
		private widgetSelectionService: InteractivityWidgetSelectionService,
		private secondsPipe: SecondsPipe,
		protected translate: TranslateService) {
		this.subscriptions = new Subscription();
	}

	@HostListener('window:keydown', ['$event'])
	onKeyDown(ev: KeyboardEvent) {
		if (ev.code === 'KeyG' && ev.ctrlKey === true) {
			const selectedTracks: ITrackAreaItem[] = this.selectedTracks
				&& this.selectedTracks.map((flat: any) => flat && flat.child && (flat.child as ITrackAreaItem)) || [];
			this.groupTracks(selectedTracks);
		}
	}

	ngOnInit() {
		this.items = [];
		const collapsedGroupsObservable = this.interactivityStateService.propSub('collapsedGroups');
		const widgetSelectionObservable = this.widgetSelectionService.widgetSelectionSubject;

		this.subscriptions.add(collapsedGroupsObservable.subscribe((collapsedGroups: any) => {
			this.collapsedGroups = collapsedGroups;
			this.flattenArray();
		}));

		this.subscriptions.add(this.interactivityEditorService.trackZoomSubject.subscribe((trackZoomInfo: any) => {
			if (trackZoomInfo) {
				this.trackZoomInfo = trackZoomInfo;
			}
		}));

		this.subscriptions.add(
			this.interactivityEditorService.durationSubject.subscribe(
				(duration: number) => {
					this.duration = duration;
				})
		);

		this.subscriptions.add(this.interactivityMediaService.currentTimeSubject.subscribe((time: number) => {
			this._currentTime = time;
			this.checkInCurrentTime(this._currentTime);
		}));

		this.subscriptions.add(
			this.interactivityEditorService.groupTagSubject.subscribe((groupTags: IWidgetGroupTag[]) => {
				if (groupTags) {
					this.widgetGroupTags = groupTags;
				} else {
					this.widgetGroupTags = [];
				}
				setTimeout(() => {
					this.scrollablePane.update();
				}, 500);
			}));

		this.subscriptions.add(combineLatest([
			collapsedGroupsObservable,
			widgetSelectionObservable
		]).subscribe((res: any[]) => {
			this.collapsedGroups = res[0];
			const selection = res[1];

			this.flattenArray();

			if (selection && selection.widgets && selection.widgets.length) {
				const selectedTracks = this.interactivityEditorService.getSelectedTracks(selection.widgets, this.collapsedGroups);
				this.selectedTracks = [...selectedTracks];
				this.selectedTrackIds = this.selectedTracks.map((track: FlatITrackAreaItem) => track.id);

				// find index of lowest selectedTrack in the flatArray
				const lastSelectedFlatTrack = this.selectedTracks[this.selectedTracks.length - 1];
				this.ghostTrackIndex = this.flatArray.findIndex(flatTrack => flatTrack &&
					lastSelectedFlatTrack && flatTrack.id === lastSelectedFlatTrack.id);

				if (lastSelectedFlatTrack && lastSelectedFlatTrack.type === 'track') { // track is added after single or multiple items
					const targetIndex = (this.items.length - 1) - (this.items.findIndex(track => track &&
						lastSelectedFlatTrack && track.id === lastSelectedFlatTrack.id));
					this.onTrackSelected.emit({ targetIndex, groupId: null });

				} else if (lastSelectedFlatTrack && lastSelectedFlatTrack.type === 'trackGroupEnd') { // track is added after a group
					const targetIndex = (this.items.length - 1) - (this.items.findIndex(track => track &&
						lastSelectedFlatTrack && track.id === lastSelectedFlatTrack.id.slice(4)));
					this.onTrackSelected.emit({ targetIndex, groupId: null });

				} else { // track is added to group
					const group = this.flatArray.find(flatTrack => flatTrack &&
						lastSelectedFlatTrack && flatTrack.id === lastSelectedFlatTrack.groupId); // find group
					if (group) {
						const groupIndex = this.flatArray.indexOf(group);
						const flatIndex = this.ghostTrackIndex - groupIndex;
						const targetIndex = ((group.child as TrackGroup).tracks.length) - flatIndex; // reverse for redux

						this.onTrackSelected.emit({ targetIndex, groupId: lastSelectedFlatTrack.groupId });
					}
				}
			} else {
				this.ghostTrackIndex = this.flatArray.length - 1;
				this.onTrackSelected.emit({ targetIndex: 0, groupId: null });
				this.selectedTracks = [];
				this.selectedTrackIds = [];
			}
		})
		);

		this.subscriptions.add(combineLatest([
			this.interactivityEditorService.tracksSubject,
			this.interactivityEditorService.widgetsSubject]
		).subscribe((res: any[]) => {
			const items: ITrackAreaItem[] = res[0];
			const widgets: IWidget[] = res[1];
			if (items && widgets) {

				const chapterIds = widgets.filter(widget => widget && widget.type === 'chapter')
					.map(widget => widget && widget.id);

				this.chapterTracks = items.filter(track => track && chapterIds.indexOf(track.widgetId) !== -1);
				const filteredTracks = items.filter(track => track && chapterIds.indexOf(track.widgetId) === -1);

				const timepoints: any[] = [];
				for (let i = 0; i < this.chapterTracks.length; i++) {
					const widget = widgets.find(w => w && w.id === this.chapterTracks[i].widgetId);
					const timepoint = {
						widgetId: widget.id,
						trackId: this.chapterTracks[i].id,
						id: this.chapterTracks[i].inOutPoints[0].id,
						time: this.chapterTracks[i].inOutPoints[0].inpoint.toString(),
						timeOut: this.chapterTracks[i].inOutPoints[0].outpoint.toString(),
						label: widget.label || 'chapter ' + (i + 1)
					};

					timepoints.push(timepoint);
				}

				this.chapterConfig = {
					label: 'CHAPTERS',
					timepoints: {
						data: timepoints
					}
				};

				this.items = [...filteredTracks];
				this.cachedAppTrack = null;
				this.flattenArray();

				this.filterItems();
				this.ghostTrackIndex = this.flatArray.length - 1;
				this.onTrackSelected.emit({ targetIndex: 0, groupId: null });
				setTimeout(() => {
					this.updateFilterableWidgetTypes();
					if (this.verticalScrollbar) {
						this.verticalScrollbar.update();
					}
					this.checkInCurrentTime(this._currentTime);
				});
			}
		}));
	}

	ngOnChanges(changes: SimpleChanges) {
		if (typeof changes.showGhost !== 'undefined' && !changes.showGhost.firstChange) {
			if (changes.showGhost.currentValue === true && changes.showGhost.previousValue === false) {
				if (this.tracksEl.length) {
					const trackArray = this.tracksEl.toArray();
					this.scrollToTrack(trackArray[this.ghostTrackIndex]);
				}
			}
		}
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	filterCurrentTrack(flatTracks, track) {
		return flatTracks.filter(ft => ft.id !== track.id);
	}

	zoom(direction: 'in' | 'out' | 'toFit') {
		let zoomInfo = null;
		const zoomStep = 0.2;

		if (direction === 'toFit') {
			zoomInfo = {
				zoomFactor: 1,
				offset: 0,
			};
		} else if (direction === 'in') {
			const newZoomFactor = Math.min(this.trackZoomInfo.zoomFactor * (1 - zoomStep), 1);
			const zoomDiff = this.trackZoomInfo.zoomFactor - newZoomFactor;
			zoomInfo = {
				zoomFactor: newZoomFactor,
				offset: Math.min(Math.max(this.trackZoomInfo.offset + zoomDiff / 2, 0), (1 - newZoomFactor)),
			};
		} else if (direction === 'out') {
			const newZoomFactor = Math.min(this.trackZoomInfo.zoomFactor / (1 - zoomStep), 1);
			const zoomDiff = this.trackZoomInfo.zoomFactor - newZoomFactor;
			zoomInfo = {
				zoomFactor: newZoomFactor,
				offset: Math.min(Math.max(this.trackZoomInfo.offset + zoomDiff / 2, 0), (1 - newZoomFactor)),
			};
		}
		if (zoomInfo) {
			this.interactivityEditorService.trackZoomSubject.next(zoomInfo);
		}
	}

	flattenArray() {
		// flatten array
		this.flatArray = [];
		for (let i = 0, l: number = this.items.length; i < l; i++) {
			const trackItem = (this.items[i] as any);
			if (trackItem instanceof Track) {
				if (trackItem.fromAppId) {
					if (!this.collapsedGroups[trackItem.fromAppTrackId]) {
						this.flatArray.push(
							{
								id: trackItem.id,
								type: 'trackFromApp',
								child: trackItem
							}
						);
					}
				} else {
					this.flatArray.push(
						{
							id: trackItem.id,
							type: 'track',
							child: trackItem
						}
					);
				}
			} else if (this.items[i] instanceof TrackGroup) {
				const groupId = trackItem.id;
				this.flatArray.push(
					{
						id: groupId,
						type: 'trackGroup',
						child: trackItem
					}
				);
				for (let j = 0, k: number = trackItem.tracks.length; j < k; j++) {
					const trackInGroup = trackItem.tracks[j];
					if (!this.collapsedGroups[groupId] ||
						(this.collapsedGroups[groupId] && this.hiddenTracks.length > 0 && this.hiddenTracks.indexOf(trackInGroup.id) === -1)) {
						this.flatArray.push(
							{
								id: trackInGroup.id,
								type: 'trackInGroup',
								groupId,
								child: trackInGroup
							}
						);
						if (j === k - 1 && (!this.hiddenTracks || this.hiddenTracks.length === 0)) {
							this.flatArray.push(
								{
									id: 'end_' + trackItem.id,
									type: 'trackGroupEnd'
								}
							);
						}
					}
				}
				if (!trackItem.tracks || trackItem.tracks.length < 1 ||
					(this.collapsedGroups[groupId] && (!this.hiddenTracks || this.hiddenTracks.length === 0))) {
					this.flatArray.push(
						{
							id: 'end_' + trackItem.id,
							type: 'trackGroupEnd'
						}
					);
				}
			}
		}
	}

	checkIfTrackInCurrentTime(track: ITrackAreaItem, time: number) {
		let ret = false;
		if (track && track.inOutPoints && track.inOutPoints.length) {
			if (track.fromAppTrackId) { // track within app
				if (!this.cachedAppTrack || this.cachedAppTrack.id !== track.fromAppTrackId) {
					this.cachedAppTrack = this.interactivityEditorService.getTrackById(track.fromAppTrackId);
				}
				if (this.cachedAppTrack) {
					track = this.cachedAppTrack; // NB track within app takes app track for time reference!
				}
			}

			track.inOutPoints.forEach((ioPoint: IInOutPoint) => {
				let inPoint: number = parseFloat(ioPoint.inpoint);
				if (typeof ioPoint.inpoint === 'string' && ioPoint.inpoint.indexOf('%') > 0) {
					inPoint = (inPoint / 100) * this.duration;
				}
				let outPoint: number = parseFloat(ioPoint.outpoint);
				if (typeof ioPoint.outpoint === 'string' && ioPoint.outpoint.indexOf('%') > 0) {
					outPoint = (outPoint / 100) * this.duration;
				}
				if (inPoint <= time && time < outPoint) {
					ret = true;
				}
			});
		}

		return ret;
	}

	checkInCurrentTime(time: number) {
		const tracksInCurrentTime: string[] = [];
		const widgetsInCurrentTime: string[] = [];
		let checkString = '';

		for (let i = 0; i < this.items.length; i++) {
			const track = this.items[i];
			if (track && track instanceof TrackGroup && track.tracks) {
				for (let j = 0; j < track.tracks.length; j++) {
					const trackInGroup = track.tracks[j];
					const trackInGroupInCurrentTime = this.checkIfTrackInCurrentTime(trackInGroup, time);
					if (trackInGroupInCurrentTime) {
						checkString += trackInGroup.id;

						tracksInCurrentTime.push(trackInGroup.id);
						widgetsInCurrentTime.push(trackInGroup.widgetId);
					}
				}
			} else {
				const trackInCurrentTime = this.checkIfTrackInCurrentTime(track, time);
				if (trackInCurrentTime) {
					checkString += track.id;

					tracksInCurrentTime.push(track.id);
					widgetsInCurrentTime.push(track.widgetId);
				}
			}
		}
		if (!this.inCurrentTime || this.inCurrentTime.checkString !== checkString) {
			this.inCurrentTime = {
				checkString,
				trackIds: tracksInCurrentTime,
				widgetIds: widgetsInCurrentTime
			};
			this.widgetSelectionService.currentlyInTimeSubject.next(this.inCurrentTime);
		}
	}

	scrollToTrack(element: ElementRef) {
		setTimeout(() => {
			if (element && element.nativeElement) {
				element.nativeElement.scrollIntoViewIfNeeded();
			}
		});
	}

	isTrack(item: ITrackAreaItem): boolean {
		return (item instanceof Track);
	}
	isTrackGroup(item: ITrackAreaItem): boolean {
		return (item instanceof TrackGroup);
	}

	trackBy(ind: number, item: any) {
		return item.id || ind;
	}

	onTrackMousedown(flatTrack: FlatITrackAreaItem, event: MouseEvent) {
		this.currentDragFlatTrack = flatTrack;
		const modKey = modKeyDown(event);
		const widget = this.interactivityEditorService.getWidgetById(flatTrack.child.widgetId) as any;

		// when dragging, let app behave as group when open, otherwise as a normal track
		if (flatTrack && flatTrack.type === 'trackGroup'
			|| widget && widget.type && widget.type === WidgetType.App && !this.collapsedGroups[flatTrack.id]) {
			if (!modKey) {
				this.widgetSelectionService.clearSelectedWidgets();
			}
			const selectedWidgetsIds: string[] = [];

			if (flatTrack.type === 'trackGroup') {
				for (let i = 0, l: number = (flatTrack.child as TrackGroup).tracks.length; i < l; i++) {
					selectedWidgetsIds.push((flatTrack.child as TrackGroup).tracks[i].widgetId);
				}
			}

			if (widget && widget.type === WidgetType.App) {
				selectedWidgetsIds.push(flatTrack.child.widgetId);
				for (let i = 0, l: number = this.items.length; i < l; i++) {
					if (this.items[i].fromAppTrackId) {
						selectedWidgetsIds.push(this.items[i].widgetId);
					}
				}
			}
			this.widgetSelectionService.addSelectedWidgetByIdMulti(selectedWidgetsIds);
		} else {
			if (!modKey) {
				if (this.selectedTracks && this.selectedTrackIds.indexOf(flatTrack.id) !== -1) {
					// do nothing
					return;
				} else {
					this.widgetSelectionService.clearSelectedWidgets();
					this.widgetSelectionService.addSelectedWidgetById(flatTrack.child.widgetId, { area: 'tracks' });
				}
			} else {
				if (this.widgetSelectionService.selectedWidgetIds) {
					const selectedWidgets = this.widgetSelectionService.selectedWidgetIds.filter((widgetId: string) => widgetId === flatTrack.child.widgetId);
					if (selectedWidgets.length > 0) {
						this.widgetSelectionService.removeSelectedWidgetById(flatTrack.child.widgetId);
					} else {
						this.widgetSelectionService.addSelectedWidgetById(flatTrack.child.widgetId, { area: 'tracks' });
					}
				}
			}
		}
	}

	onItemDropped(event: CdkDragDrop<any[]>) {
		const oldOrder = event.previousContainer.data;
		const newOrder = event.container.data;
		let itemAboveDraggedItem;
		let itemBelowDraggedItem;

		if (event.previousIndex < event.currentIndex) {
			itemAboveDraggedItem = oldOrder[event.currentIndex];
			itemBelowDraggedItem = oldOrder[event.currentIndex + 1];
		} else {
			itemAboveDraggedItem = oldOrder[event.currentIndex - 1];
			itemBelowDraggedItem = oldOrder[event.currentIndex];
		}

		let currentDragWidget;
		if (this.currentDragFlatTrack && this.currentDragFlatTrack.child && this.currentDragFlatTrack.child.widgetId) {
			currentDragWidget = this.interactivityEditorService.getWidgetById(this.currentDragFlatTrack.child.widgetId) as any;
		}

		if ((currentDragWidget && currentDragWidget.type === WidgetType.App &&
			// app tracks cannot be dragged in groups
			(itemAboveDraggedItem && (itemAboveDraggedItem.type === 'trackInGroup' || itemAboveDraggedItem.type === 'trackGroup')))
			|| (itemBelowDraggedItem && (itemBelowDraggedItem.type === 'trackFromApp'
				// other tracks cannot be dragged in apps
				|| itemAboveDraggedItem && itemAboveDraggedItem.type === 'trackFromApp' && itemBelowDraggedItem.type !== 'track'))) {
			return;
		}

		if (this.selectedTracks.length < 2) { // no multi-drag, dispatch new order
			moveItemInArray(newOrder, event.previousIndex, event.currentIndex);
		} else {
			this.moveMultipleItems(oldOrder, newOrder, event.currentIndex);
		}

		this.currentDragFlatTrack = null;

		const newArray = [];
		const trackIdsInGroup = [];

		for (let i = 0, l: number = newOrder.length; i < l; i++) {  // de-flatten array
			const trackItem = newOrder[i];
			if (trackItem.type === 'trackGroup') {
				const groupIndex = i;
				const groupEndIndex = newOrder.findIndex(track => track && track.id === 'end_' + trackItem.id);

				// copy so we don't alter the displayed array
				const trackArray = [...newOrder].slice(groupIndex + 1, groupEndIndex).map(flatTrack => flatTrack && flatTrack.child);
				const trackIdArray = trackArray.map(track => track && track.id);

				if (!this.collapsedGroups[trackItem.id]) {
					trackItem.child.tracks = [];
				}
				trackItem.child.tracks.push(...trackArray);
				trackIdsInGroup.push(...trackIdArray);

				newArray.push(trackItem.child);
			} else if (trackIdsInGroup.indexOf(trackItem.id) === -1 && (trackItem.type === 'track' || trackItem.type === 'trackInGroup')) {
				newArray.push(trackItem.child);
			}
		}
		this.interactivityEditorService.dispatchNewOrder(newArray, this.chapterTracks);
	}

	moveMultipleItems(oldOrder: FlatITrackAreaItem[], newOrder: FlatITrackAreaItem[], currentIndex: number) {
		const dragTrackId = this.currentDragFlatTrack.id;

		for (let i = 0, l: number = this.selectedTracks.length; i < l; i++) {
			const prevIndex = oldOrder.findIndex(track => track && track.id === this.selectedTracks[i].id);
			if (prevIndex < currentIndex && dragTrackId !== this.selectedTracks[i].id) {
				currentIndex--;
			}
			newOrder.splice(prevIndex, 1); // remove selected tracks

		}

		// if items are dropped inside a group and not in itself
		if (oldOrder[currentIndex - 1] &&
			(oldOrder[currentIndex - 1].type === 'trackInGroup' || oldOrder[currentIndex - 1].type === 'trackGroup')) {
			const selectedTracksInGroup = this.selectedTracks.filter((selectedFlatTrack: FlatITrackAreaItem) =>
				selectedFlatTrack.type === 'track' || selectedFlatTrack.type === 'trackInGroup');

			const selectedCollapsedGroups = this.selectedTracks.filter((selectedFlatTrack: FlatITrackAreaItem) =>
				selectedFlatTrack.type === 'trackGroup' && this.collapsedGroups[selectedFlatTrack.id]);

			if (selectedCollapsedGroups && selectedCollapsedGroups.length) { // check for collapsed group(s)
				for (let i = 0, l: number = selectedCollapsedGroups.length; i < l; i++) {
					for (let j = 0, k: number = (selectedCollapsedGroups[i].child as TrackGroup).tracks.length; j < k; j++) {
						const trackInGroup = (selectedCollapsedGroups[i].child as TrackGroup).tracks[j];
						selectedTracksInGroup.push(
							{
								id: trackInGroup.id,
								type: 'trackInGroup',
								groupId: selectedCollapsedGroups[i].id,
								child: trackInGroup
							}
						);
					}
				}
			}
			newOrder.splice(currentIndex, 0, ...selectedTracksInGroup); // paste selected tracks within group

		} else {
			newOrder.splice(currentIndex, 0, ...this.selectedTracks); // paste selected tracks
		}
	}

	public onTrackDragStart(track: ITrackAreaItem, event: CdkDragStart<any[]>) {
		this.draggingTrack = true;
	}

	public onTrackDragEnd(track: ITrackAreaItem, event: CdkDragEnd<any[]>) {
		this.draggingTrack = false;
	}

	toggleWidgetTypeFilter(widgetType: string) {
		const ind = this.activeWidgetTypeFilters.indexOf(widgetType);
		if (ind === -1) {
			this.activeWidgetTypeFilters.push(widgetType);
		} else {
			this.activeWidgetTypeFilters.splice(ind, 1);
		}
		this.filterItems();
	}

	toggleWidgetGroupTag(widgetGroupTag: IWidgetGroupTag) {
		const ind = this.activeWidgetGroupIDs.indexOf(widgetGroupTag.id);
		if (ind === -1) {
			this.activeWidgetGroupIDs.push(widgetGroupTag.id);
		} else {
			this.activeWidgetGroupIDs.splice(ind, 1);
		}
		this.filterItems();
	}

	checkIfTrackShouldHide(track: Track) {
		if (!track.widgetId) {
			return false;
		}
		const widget = this.interactivityEditorService.getWidgetById(track.widgetId) as any;
		const freeTextFiltered = this.freeTextFilter &&
			(!widget || !widget.label || widget.label.toLowerCase().indexOf(this.freeTextFilter) === -1);
		const typeFiltered = widget && this.activeWidgetTypeFilters.length && this.activeWidgetTypeFilters.indexOf(widget.type) === -1;

		let groupTagFiltered = !!this.activeWidgetGroupIDs.length;
		if (groupTagFiltered && widget && widget.grouping && widget.grouping.length) {
			widget.grouping.forEach((groupId: string) => {
				if (groupTagFiltered && this.activeWidgetGroupIDs.indexOf(groupId) !== -1) {
					groupTagFiltered = false;
				}
			});
		}

		return (freeTextFiltered || typeFiltered || groupTagFiltered);

	}

	updateFilterableWidgetTypes() {
		this.filterableWidgetTypes = [];
		this.items.forEach((track) => {
			if (track && track.widgetId) {
				const widget = this.interactivityEditorService.getWidgetById(track.widgetId) as any;
				if (widget && widget.type && this.filterableWidgetTypes.indexOf(widget.type) === -1) {
					this.filterableWidgetTypes.push(widget.type);
				}
			} else if (track && (track as TrackGroup).tracks) {
				(track as TrackGroup).tracks.forEach((trackInGroup) => {
					const widget = this.interactivityEditorService.getWidgetById(trackInGroup.widgetId) as any;
					if (widget && widget.type && this.filterableWidgetTypes.indexOf(widget.type) === -1) {
						this.filterableWidgetTypes.push(widget.type);
					}
				});
			}
		});
	}

	filterItems() {
		const hiddenTracks: string[] = [];

		this.items.forEach((track: ITrackAreaItem) => {
			if (track instanceof TrackGroup && track.tracks && track.tracks.length) {
				let visibleTracks = track.tracks.length;
				track.tracks.forEach((trackInGroup: Track) => {
					if (this.checkIfTrackShouldHide(trackInGroup)) {
						hiddenTracks.push(trackInGroup.id);
						visibleTracks--;
					}
				});
				if (visibleTracks === 0) {
					hiddenTracks.push(track.id);
				}
			} else {
				if (track instanceof Track && this.checkIfTrackShouldHide(track)) {
					hiddenTracks.push(track.id);
				}
			}
		});
		this.hiddenTracks = hiddenTracks;
		this.flattenArray();
		setTimeout(() => {
			this.scrollablePane.update();
		}, 500);
	}

	clearFilters() {
		this.activeWidgetTypeFilters = [];
		this.activeWidgetGroupIDs = [];
		this.freeTextFilter = '';
		this.filterItems();
	}

	onFilterInput(ev: Event, el: HTMLInputElement) {
		this.filterItems();
	}

	editTrack(editing: boolean) {
		this.editMode = editing;
		setTimeout(() => {
			if (this.verticalScrollbar) {
				this.verticalScrollbar.update();
			}
		});
	}

	contextMenuHide(): void {
		this.contextMenuStyle.display = 'none';
	}

	onTrackContextMenu(ev: any, item: any): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}

		const selectedTracks: ITrackAreaItem[] = this.selectedTracks
			&& this.selectedTracks.map((flat: any) => flat && flat.child && (flat.child as ITrackAreaItem)) || [];
		const selectedWidgets = this.widgetSelectionService.widgetSelection.widgets;

		this.contextMenuOptions = [];

		// Ungroup
		if (item && item.child instanceof TrackGroup) {
			this.contextMenuOptions.push({
				item: item.child,
				label: `Ungroup "${item.child.label || ''}"`,
				value: 'ungroup',
				icon: 'folder-open-o'
			});
		}

		// Move in/outpoints
		if (item && item instanceof Track && item.inOutPoints && item.inOutPoints[0]) {
			// const inpoint = parseFloat(item.inOutPoints[0].inpoint);
			// const outpoint = parseFloat(item.inOutPoints[0].outpoint);
			this.contextMenuOptions.push({
				item,
				label: `Move in-point to ${this.secondsPipe.transform(this._currentTime, 'withFrames')}`,
				value: 'move_inpoint',
				onSelected: () => {
					selectedTracks.forEach((t) => {
						// const inp = parseFloat(t.inOutPoints[0].inpoint);
						const outp = parseFloat(t.inOutPoints[0].outpoint);

						const newIn = this._currentTime;
						const newOut = newIn > outp ? newIn + 2 : outp;

						this.interactivityEditorService.updateInOutPoint(t.id, t.inOutPoints[0], String(newIn), String(newOut));
					});
				},
				iconText: '['
			});

			this.contextMenuOptions.push({
				item,
				label: `Move out-point to ${this.secondsPipe.transform(this._currentTime, 'withFrames')}`,
				value: 'move_outpoint',
				onSelected: () => {
					selectedTracks.forEach((t) => {
						const inp = parseFloat(t.inOutPoints[0].inpoint);
						// const outp = parseFloat(t.inOutPoints[0].outpoint);

						const newOut = this._currentTime;
						const newIn = newOut < inp ? newOut - 2 : inp;

						this.interactivityEditorService.updateInOutPoint(t.id, t.inOutPoints[0], String(newIn), String(newOut));
					});
				},
				iconText: ']'
			});
		}
		// Group
		if (item && item instanceof Track && selectedTracks &&
			(!selectedTracks.some(track => track && track.id === item.id) || selectedTracks.length === 1)) { // track not in selection or exclusively
			if (!this.flatArray.some((flat: any) => flat && flat.id === item.id && flat.groupId)) { // not already grouped
				const widget = this.interactivityEditorService.getWidgetById(item.widgetId);
				if (widget && !widget.fromAppId) {
					this.contextMenuOptions.push({
						item,
						label: `Group "${widget.label || ''}"`,
						value: 'group',
						icon: 'folder-o'
					});
				}
			}
		} else if (selectedTracks && selectedTracks.length > 0) { // selection
			if (!selectedTracks.some(track => track instanceof TrackGroup) && // no trackgroup
				!this.selectedTracks.some((flat: any) => flat && flat.groupId)) { // no track that's already grouped
				this.contextMenuOptions.push({
					item: selectedTracks,
					label: `Group ${selectedWidgets.length || ''} widgets`,
					value: 'group',
					icon: 'folder-o'
				});
			}
		}

		// Delete
		if (item && item instanceof Track && selectedTracks &&
			(!selectedTracks.some(track => track && track.id === item.id) || selectedTracks.length === 1)) { // track not in selection or exclusively
			const widget = this.interactivityEditorService.getWidgetById(item.widgetId);
			if (widget && !widget.fromAppId) { // normal widget track
				this.contextMenuOptions.push({
					item,
					label: `Delete "${widget.label || ''}"`,
					value: 'delete',
					icon: 'trash'
				});
			}
		} else if (selectedTracks && selectedTracks.length > 0) { // selection
			this.contextMenuOptions.push({
				item: selectedTracks,
				label: 'Delete ' + selectedWidgets.length + ' widgets',
				value: 'delete',
				icon: 'trash'
			});
		}

		// Hide / Show
		if (item && item instanceof Track && selectedTracks &&
			(!selectedTracks.some(track => track && track.id === item.id) || selectedTracks.length === 1)) { // track not in selection or exclusively
			const track = item as Track;
			const widget = this.interactivityEditorService.getWidgetById(track.widgetId);
			if (widget instanceof StageWidget) {
				if ((widget as StageWidget).visibility !== 'hidden') {
					this.contextMenuOptions.push({
						item,
						label: `Hide "${widget.label || ''}"`,
						value: 'hide',
						icon: 'eye-slash'
					});
				} else {
					this.contextMenuOptions.push({
						item,
						label: `Show "${widget.label || ''}"`,
						value: 'show',
						icon: 'eye'
					});
				}
			}
		} else if (selectedTracks && selectedTracks.length > 0) { // selection
			// nothing
		}

		this.contextMenuShow(ev.pageX, ev.pageY);
	}

	onTrackElementContextMenu(ev: any, item: any): void {
		this.onTrackContextMenu(ev, item); // for now, until multiple track elements per track
	}

	onContextMenuOptionSelected(option: ContextMenuOption): void {
		switch (option.value) {
			case 'delete':
				if (option.item instanceof Track) {
					const track = option.item as Track;
					this.deleteTracks([track]);
				} else if (option.item instanceof Array) {
					this.deleteTracks(option.item);
				}
				break;

			case 'group':
				if (option.item instanceof Track) {
					const track = option.item as Track;
					this.groupTracks([track]);
				} else if (option.item instanceof Array) {
					this.groupTracks(option.item);
				}
				break;

			case 'ungroup':
				if (option.item instanceof TrackGroup) {
					this.interactivityEditorService.ungroup(option.item);
				}
				break;

			case 'hide':
				if (option.item instanceof Track) {
					const track = option.item as Track;
					const widget = this.interactivityEditorService.getWidgetById(track.widgetId);
					if (widget instanceof StageWidget) {
						this.interactivityEditorService.toggleWidgetVisibility(widget);
					}
				}
				break;

			case 'show':
				if (option.item instanceof Track) {
					const track = option.item as Track;
					const widget = this.interactivityEditorService.getWidgetById(track.widgetId);
					if (widget instanceof StageWidget) {
						this.interactivityEditorService.toggleWidgetVisibility(widget);
					}
				}
				break;
		}
		this.contextMenuHide();
	}


	/**
	 * @param tracks ITrackAreaItem[] to group
	 */
	private groupTracks(tracks: ITrackAreaItem[]) {
		if (tracks && tracks.length && !tracks.some(item => item instanceof TrackGroup)) {
			const selectedWidgetTypes = [];
			for (let i = 0; i < tracks.length; i++) {
				const selectedTrack = tracks[i];
				const widget = this.interactivityEditorService.getWidgetById(selectedTrack.widgetId);
				if (widget) {
					selectedWidgetTypes.push(widget.type);
				}
			}
			if (selectedWidgetTypes.indexOf(WidgetType.App) === -1) {
				this.interactivityEditorService.createTrackGroup(tracks);
			}
		}
	}

	/**
	 * @param tracks ITrackAreaItem[] to delete
	 */
	private deleteTracks(tracks: ITrackAreaItem[]) {
		this.widgetSelectionService.deleteSelectedWidgets(); // REFACTOR ME: implement in InteractivityEditorService

		// delete trackgroup(s)
		if (tracks && tracks.length) {
			const groupIds: string[] = tracks
				.filter(item => item && item instanceof TrackGroup)
				.map(item => item && item.id);
			for (let ig = 0, lg = groupIds.length; ig < lg; ig++) { // REFACTOR ME: single call
				this.interactivityEditorService.removeTrackGroup(groupIds[ig]);
			}
		}
	}

	private contextMenuShow(posX: number, posY: number): void {
		this.contextMenuStyle.display = 'block';
		this.contextMenuStyle.left = posX + 'px';
		this.contextMenuStyle.top = posY + 'px';
	}
}
